<template>
  <div
    class="avatar"
    :class="[`avatar--${size}`, `avatar--${variant}`, { 'avatar--editable': editable }]"
    @click="handleClick"
  >
    <img
      v-if="src && !imageFailed"
      :src="src"
      :alt="alt"
      class="avatar__image"
      @error="handleImageError"
    />
    <div v-else class="avatar__placeholder">
      <span class="avatar__initials">{{ initials }}</span>
    </div>
    <div v-if="editable" class="avatar__edit-overlay">
      <span class="avatar__edit-icon">✏️</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

interface Props {
  src?: string | null
  alt?: string
  name?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'default' | 'subtle'
  editable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  src: null,
  alt: 'Avatar',
  name: '',
  size: 'md',
  variant: 'default',
  editable: false
})

const emit = defineEmits<{
  click: []
}>()

const imageFailed = ref(false)

// Retry loading when the image URL changes after a previous failure.
watch(() => props.src, () => {
  imageFailed.value = false
})

const initials = computed(() => {
  if (!props.name) return '?'
  const parts = props.name.trim().split(' ').filter(p => p.length > 0)
  if (parts.length === 0) return '?'

  const firstInitial = parts[0]?.[0] || '?'
  if (parts.length >= 2) {
    const lastInitial = parts[parts.length - 1]?.[0] || '?'
    return (firstInitial + lastInitial).toUpperCase()
  }
  return firstInitial.toUpperCase()
})

function handleImageError() {
  imageFailed.value = true
}

function handleClick() {
  if (props.editable) {
    emit('click')
  }
}
</script>

<style scoped>
.avatar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar--default {
  background: linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-gradient-end) 100%);
}

.avatar--subtle {
  background: var(--color-brand-primary-overlay-light);
}

.avatar--xs {
  width: 24px;
  height: 24px;
}

.avatar--sm {
  width: 32px;
  height: 32px;
}

.avatar--md {
  width: 48px;
  height: 48px;
}

.avatar--lg {
  width: 64px;
  height: 64px;
}

.avatar--xl {
  width: 96px;
  height: 96px;
}

.avatar--editable {
  cursor: pointer;
}

.avatar--editable:hover .avatar__edit-overlay {
  opacity: 1;
}

.avatar__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar__initials {
  font-weight: var(--font-weight-semibold);
  user-select: none;
}

.avatar--default .avatar__initials {
  color: var(--color-text-inverse);
}

.avatar--subtle .avatar__initials {
  color: var(--color-brand-primary);
  font-weight: var(--font-weight-medium);
}

.avatar--xs .avatar__initials {
  font-size: var(--font-size-xs);
}

.avatar--sm .avatar__initials {
  font-size: var(--font-size-sm);
}

.avatar--md .avatar__initials {
  font-size: var(--font-size-md);
}

.avatar--lg .avatar__initials {
  font-size: var(--font-size-lg);
}

.avatar--xl .avatar__initials {
  font-size: var(--font-size-2xl);
}

.avatar__edit-overlay {
  position: absolute;
  inset: 0;
  background: var(--color-overlay-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-duration-normal) var(--transition-easing-ease-out);
}

.avatar__edit-icon {
  font-size: var(--font-size-lg);
}
</style>
